import React, { useCallback, useEffect, useState } from 'react';
import { observer } from 'mobx-react';
import { debounce } from 'throttle-debounce';

import { useAppManager } from 'apps/hooks';
import { clientStore } from 'apps/client/app';
import { routerStore } from 'core/routing';
import ProjectsAppManager from 'apps/projects/appManager';
import { CardSection } from 'legacyComponents/layout/cardSection';
import { ProjectCard } from 'legacyComponents/cards/types/project';

import { fromLocalStorage } from 'core/localStorage';
import { EmptyProjectWithCreate } from 'legacyComponents/layout/EmptyProjectWithCreate';
import { useViewType } from 'hooks/frameStoreHooks';
import { frameStore } from 'apps/components/Frame/appManager';
import { ViewTypeEnum } from 'types';
import { ProjectsSearch } from './ProjectsSearch';
import { ProjectListItem } from './ProjectListItem';

const Projects = observer(({ maxItems }: { maxItems?: number }) => {
  const shouldCreate = !!clientStore.signedInProfile;
  const appManager = useAppManager(ProjectsAppManager, null, {}, shouldCreate);

  useViewType(ViewTypeEnum.Card);

  const { viewType } = frameStore;
  const projects: any = appManager?.projectsConn?.arrayApp;

  useEffect(() => {
    frameStore.SetProjectSortInfo({
      projectsConn: appManager?.projectsConn,
    });
    return () => {
      frameStore.SetProjectSortInfo(null);
    };
  }, [appManager?.projectsConn]);

  const [searchText, setSearchText] = useState('');
  // debounce so we do not query every character
  const debounceSetProjectsFilter = useCallback(
    debounce(500, (newSearchText: string) => {
      appManager.setProjectsQueryFilter(newSearchText);
    }),
    [],
  );
  const handleNewSearchText = (newSearchText: string) => {
    setSearchText(newSearchText);

    if (newSearchText) debounceSetProjectsFilter(newSearchText);
    else {
      // bypass debounce if we are clearing the filter
      // cancel any pending debounce
      debounceSetProjectsFilter.cancel({ upcomingOnly: true });
      appManager.setProjectsQueryFilter(newSearchText);
    }
  };

  useEffect(() => {
    // reset filter when switching between spaces
    // e.g. a @profile -> +initiative or +initiative1 to +initiative2
    handleNewSearchText('');
  }, [routerStore.route]);

  if (!clientStore.signedInProfile) return null;

  const { projectsConn } = appManager;

  if (!projectsConn || !projects) return null;

  const numItems = projects.length || 0;

  const deletingProjectId = fromLocalStorage('deletingProject')?.id;

  if (deletingProjectId) {
    projects.apps.forEach((app: Record<string, any>) => {
      if (app.id === deletingProjectId) {
        app.deleting = true;
      }
    });
  }

  if (appManager.fileSyncedProjectIds.length > 0) {
    projects.apps.forEach(app => {
      app.fileSyncConnected = appManager.fileSyncedProjectIds.includes(app.id);
    });
    projects.sort((a, b) => {
      if (a.fileSyncConnected && !b.fileSyncConnected) return -1;
      if (b.fileSyncConnected && !a.fileSyncConnected) return 1;
      return 0;
    });
  }

  // hacky code to make things align because the inner shape of
  // CardSection would need to be reworked.
  let wrapperMarginTop = 0;
  if (numItems > 0) {
    wrapperMarginTop = viewType === ViewTypeEnum.List ? 16 : 12;
  }

  const isFilterHidingContent =
    searchText && numItems === 0 && !projectsConn.loading;

  const mainContent = isFilterHidingContent ? (
    <EmptyProjectWithCreate
      emptyText='No projects found for searched terms.'
      hideButton
      style={{ marginTop: 28 }}
    />
  ) : (
    <CardSection
      invisible={true}
      subTitle=' '
      tooltip='Create new Project.'
      owner
      viewType={viewType}
      ownerName={clientStore.signedInProfile.user.fullName}
      cardApp={projects}
      CardComponent={
        viewType === ViewTypeEnum.List ? ProjectListItem : ProjectCard
      }
      noDataCardType='homeProject'
      gridColumns={3}
      maxItems={maxItems}
      wrapperStyle={{
        marginBottom: 32,
        marginTop: wrapperMarginTop,
      }}
      showAllRouteName='workspace.spaceProjects'
      showAllRouteParams={{ spacePath: clientStore.activeSpace?.path }}
    />
  );

  return (
    <>
      <ProjectsSearch
        searchText={searchText}
        setSearchText={handleNewSearchText}
      />
      {mainContent}
    </>
  );
});

export default Projects;
